// src/tabs/GeradorAnunciosSubTab.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Sparkles,
  Save,
  RefreshCw,
  Eye,
  Edit3,
  ArrowRight,
  ListCollapse,
  Plus
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { getBaseSku } from "../utils/skuUtils";
import IaPromptsSubTab from "./IaPromptsSubTab";
import { aiPipelineService, isLikelyAdTypePrompt } from "../services/aiPipelineService";

const formatPromptOptionLabel = (p) => {
  if (!p) return "";
  const ctx = (p.contexto || "").trim();
  const nome = (p.nome || "").trim();
  const defaultPrefixCat = `Agente Redator — Categoria ${ctx}`.toLowerCase();
  const defaultPrefixAd = `Agente Normalizador — Canal ${ctx}`.toLowerCase();

  if (!nome || nome.toLowerCase() === ctx.toLowerCase()) return ctx || nome;
  if (nome.toLowerCase() === defaultPrefixCat || nome.toLowerCase() === defaultPrefixAd) {
    return `${ctx} (${nome})`;
  }
  return ctx ? `${ctx} — ${nome}` : nome;
};

export default function GeradorAnunciosSubTab({ user, findCatalogItem }) {
  const [activeSubTab, setActiveSubTab] = useState("generator");
  const [sku, setSku] = useState("");
  const [produtoNome, setProdutoNome] = useState("");
  const [categoria, setCategoria] = useState("Anel");
  const [tipoAnuncio, setTipoAnuncio] = useState("Site");
  const [categoriaPromptId, setCategoriaPromptId] = useState("");
  const [tipoAnuncioPromptId, setTipoAnuncioPromptId] = useState("");
  const [modelo, setModelo] = useState("");
  const [descricaoInput, setDescricaoInput] = useState("");

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const [rawOutputs, setRawOutputs] = useState([]); // Array to store outputs of each step
  const [result, setResult] = useState(null); // The final structured JSON object

  const [editMode, setEditMode] = useState(true); // Toggle edit/preview
  const [activeProvider, setActiveProvider] = useState(null);
  const [allProviders, setAllProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState("");

  // Carregamento de Prompts sincronizado para vincular opções nos seletores
  const [prompts, setPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);

  const fetchPrompts = useCallback(async () => {
    setLoadingPrompts(true);
    try {
      const records = await pb.collection("ia_prompts").getFullList({
        sort: "ordem"
      });
      const activeRecords = (records || []).filter(p => p.ativo !== false);
      setPrompts(activeRecords);
    } catch (e) {
      console.error("Erro loading prompts:", e);
    } finally {
      setLoadingPrompts(false);
    }
  }, []);

  // Carrega ao montar e escuta atualizações disparadas pela aba de configuração de prompts
  useEffect(() => {
    fetchPrompts();

    const handlePromptsUpdated = () => {
      fetchPrompts();
    };
    window.addEventListener("ia-prompts-updated", handlePromptsUpdated);

    return () => {
      window.removeEventListener("ia-prompts-updated", handlePromptsUpdated);
    };
  }, [fetchPrompts]);

  // Sempre recarrega os prompts ao voltar para a sub-aba do Gerador
  useEffect(() => {
    if (activeSubTab === "generator") {
      fetchPrompts();
    }
  }, [activeSubTab, fetchPrompts]);

  // Lista de agentes de Categoria (1ª Etapa)
  const categoryPrompts = useMemo(() => {
    return prompts.filter(p => p.tipo === "categoria" && !isLikelyAdTypePrompt(p));
  }, [prompts]);

  // Lista de agentes de Tipo de Anúncio / Canal (2ª Etapa) — inclui qualquer pipe de canal mesmo se salvo com tipo categoria/modelo
  const adTypePrompts = useMemo(() => {
    return prompts.filter(p => p.tipo === "tipo_anuncio" || isLikelyAdTypePrompt(p));
  }, [prompts]);

  // Sincroniza o prompt de categoria selecionado
  useEffect(() => {
    if (categoryPrompts.length > 0) {
      const currentExists = categoryPrompts.find(p => p.id === categoriaPromptId);
      if (!currentExists) {
        const byContext = categoryPrompts.find(
          p => (p.contexto || "").toLowerCase() === (categoria || "").toLowerCase()
        );
        const chosen = byContext || categoryPrompts[0];
        setCategoriaPromptId(chosen.id);
        setCategoria(chosen.contexto || chosen.nome);
      }
    }
  }, [categoryPrompts, categoriaPromptId, categoria]);

  // Sincroniza o prompt de Tipo de Anúncio selecionado
  useEffect(() => {
    if (adTypePrompts.length > 0) {
      const currentExists = adTypePrompts.find(p => p.id === tipoAnuncioPromptId);
      if (!currentExists) {
        const byContext = adTypePrompts.find(
          p => (p.contexto || "").toLowerCase() === (tipoAnuncio || "").toLowerCase()
        );
        const chosen = byContext || adTypePrompts[0];
        setTipoAnuncioPromptId(chosen.id);
        setTipoAnuncio(chosen.contexto || chosen.nome);
      }
    }
  }, [adTypePrompts, tipoAnuncioPromptId, tipoAnuncio]);

  const selectedCategoryAgent = useMemo(
    () => categoryPrompts.find(p => p.id === categoriaPromptId) || null,
    [categoryPrompts, categoriaPromptId]
  );

  const selectedAdTypeAgent = useMemo(
    () => adTypePrompts.find(p => p.id === tipoAnuncioPromptId) || null,
    [adTypePrompts, tipoAnuncioPromptId]
  );

  // Load AI configs
  useEffect(() => {
    const fetchActiveProvider = async () => {
      try {
        const records = await pb.collection("ia_configs").getFullList({
          sort: "-ativo,nome_exibicao"
        });
          setAllProviders(records || []);
          const active = (records || []).find(r => r.ativo) || records?.[0] || null;
          if (active) {
            setActiveProvider(active);
          }
        } catch (e) {
          console.error("Erro loading active AI provider:", e);
        }
      };
      fetchActiveProvider();
    }, [activeSubTab]);

  // Autofill name, model and description (Ficha Técnica) if SKU is typed
  useEffect(() => {
    let active = true;
    const loadProductData = async () => {
      const skuVal = sku.trim();
      if (!skuVal) return;
      const normalizedSku = skuVal.toUpperCase();

      // 1. Preenche campos do catálogo (nome, modelo)
      if (findCatalogItem) {
        const item = findCatalogItem(normalizedSku);
        if (item && active) {
          setProdutoNome(item.name || "");
          setModelo(item.model || "");
        }
      }

      // 2. Busca Ficha Técnica na coleção produto_dados
      const cleanPai = getBaseSku(normalizedSku);
      try {
        const records = await pb.collection("produto_dados").getFullList({
          filter: `sku_pai = "${cleanPai}"`
        });
        if (active) {
          if (records.length > 0 && records[0].ficha_tecnica) {
            const ft = records[0].ficha_tecnica;

            // Formata a ficha técnica em texto legível para a IA
            let ftText = "Especificações Ficha Técnica:\n";
            if (ft.peso_bruto_g || ft.peso_gramas) ftText += `- Peso Bruto: ${ft.peso_bruto_g || ft.peso_gramas}g\n`;
            if (ft.peso_liquido_g) ftText += `- Peso Líquido: ${ft.peso_liquido_g}g\n`;
            if (ft.liga_metal) ftText += `- Liga/Metal: ${ft.liga_metal}\n`;
            if (ft.tempo_producao_minutos) ftText += `- Tempo de Produção: ${ft.tempo_producao_minutos} min\n`;
            if (ft.qtd_pedras) ftText += `- Qtd de Pedras: ${ft.qtd_pedras}\n`;
            if (Array.isArray(ft.detalhes_pedras) && ft.detalhes_pedras.length > 0) {
              ftText += `- Detalhes das Pedras:\n`;
              ft.detalhes_pedras.forEach((p) => {
                ftText += `  • ${p.qtd || 1}x ${p.tipo || "Pedra"} (${p.tamanho || "N/D"})\n`;
              });
            }
            setDescricaoInput(ftText);
          } else {
            // Fallback padrão se não houver Ficha Técnica
            if (findCatalogItem) {
              const item = findCatalogItem(normalizedSku);
              const desc = item?.full_data?.description_full || item?.full_data?.description || item?.description || "";
              setDescricaoInput(desc || `Joia confeccionada em Prata 925 / Ouro. Código SKU: ${cleanPai}. Excelente acabamento.`);
            }
          }
        }
      } catch (err) {
        console.warn("Aviso ao carregar Ficha Técnica no GeradorAnunciosSubTab:", err);
      }
    };

    loadProductData();
    return () => { active = false; };
  }, [sku, findCatalogItem]);

  // Execute Pipeline via aiPipelineService Centralizado
  const runAiPipeline = async () => {
    if (!sku.trim()) return alert("Digite o SKU.");
    if (!produtoNome.trim()) return alert("Digite o nome da peça.");
    if (!descricaoInput.trim()) return alert("Forneça uma breve descrição ou especificações da peça.");

    setLoading(true);
    setResult(null);
    setRawOutputs([]);

    try {
      const response = await aiPipelineService.runProductPipeline({
        sku: sku.trim().toUpperCase(),
        nome: produtoNome.trim(),
        categoria,
        modelo,
        descricaoTecnica: descricaoInput,
        tipoAnuncio,
        categoriaPromptId,
        tipoAnuncioPromptId,
        preferredProviderId: selectedProviderId,
        onStep: (stepMsg) => setCurrentStep(stepMsg)
      });

      setRawOutputs(response.rawOutputs || []);
      setResult(response.result);
      setEditMode(true);
    } catch (err) {
      console.error("Falha ao rodar pipeline de IA:", err);
      alert("Falha ao rodar pipeline de IA: " + err.message);
    } finally {
      setLoading(false);
      setCurrentStep("");
    }
  };

  // Save generated advert to DB
  const handleSaveAdvert = async () => {
    if (!result) return;
    try {
      await pb.collection("anuncios_gerados").create({
        sku: sku.toUpperCase().trim(),
        produto_nome: produtoNome.trim(),
        tipo_anuncio: tipoAnuncio,
        categoria,
        modelo,
        descricao_input: descricaoInput,
        resultado: result,
        prompts_usados: rawOutputs,
        provedor_usado: activeProvider ? activeProvider.nome_exibicao : "Padrão",
        modelo_usado: activeProvider ? activeProvider.modelo_padrao : "Padrão",
        status: "concluido"
      });
      alert("Anúncio gerado salvo com sucesso no histórico!");
    } catch (err) {
      alert("Erro ao salvar anúncio: " + err.message);
    }
  };

  // Update specific fields of generated copy
  const handleFieldChange = (field, value) => {
    setResult(prev => ({ ...prev, [field]: value }));
  };

  const handleKeywordsChange = (value) => {
    const list = value.split(",").map(k => k.trim());
    setResult(prev => ({ ...prev, palavras_chave: list }));
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 text-xs">

      {/* NAVEGAÇÃO INTERNA DE SUB-ABAS (ROXO/PURPLE BRANDING) */}
      <div className="flex items-center justify-between px-6 pt-3 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveSubTab("generator")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-[10px] md:text-xs transition-all uppercase tracking-wider whitespace-nowrap ${activeSubTab === "generator"
                ? "border-purple-600 text-purple-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
          >
            <Sparkles size={14} /> Gerador de Anúncios
          </button>

          <button
            onClick={() => setActiveSubTab("prompts")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-[10px] md:text-xs transition-all uppercase tracking-wider whitespace-nowrap ${activeSubTab === "prompts"
                ? "border-purple-600 text-purple-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
          >
            <ListCollapse size={14} /> Configurar Prompts (Pipeline)
            <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-extrabold">
              {prompts.length}
            </span>
          </button>
        </div>

        <button
          type="button"
          onClick={fetchPrompts}
          disabled={loadingPrompts}
          className="mb-1.5 px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold text-[10px] flex items-center gap-1.5 shrink-0"
          title="Recarregar agentes e tipos de anúncio do banco"
        >
          <RefreshCw size={12} className={loadingPrompts ? "animate-spin text-purple-600" : ""} />
          Atualizar Seletores
        </button>
      </div>

      {/* CONTEÚDO DAS SUB-ABAS */}
      <div className="flex-1 overflow-hidden min-h-0 relative">
        {activeSubTab === "generator" ? (
          <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50 text-xs">

            {/* LEFT FORM INPUTS */}
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                  <div className="flex flex-wrap justify-between items-center border-b border-slate-100 pb-3 gap-2">
                    <div>
                      <h2 className="text-sm font-extrabold text-slate-800 flex items-center gap-1.5">
                        <Sparkles className="text-purple-650" /> Gerador de Anúncios Comercial e SEO com IA
                      </h2>
                      <p className="text-[10px] text-slate-400">Automatize descrições completas e otimizadas em HTML de novos produtos de forma sofisticada.</p>
                    </div>
                    <div className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-lg">
                      <span className="text-[9px] font-bold text-purple-700 uppercase">Motor IA:</span>
                      <select
                        value={selectedProviderId}
                        onChange={(e) => setSelectedProviderId(e.target.value)}
                        className="bg-transparent text-purple-900 font-extrabold text-[10px] outline-none cursor-pointer"
                        title="Escolha o provedor principal ou deixe em Automático (com failover entre Gemini e Claude)"
                      >
                        <option value="">
                          Automático ({activeProvider ? activeProvider.nome_exibicao : "Padrão"} + Failover)
                        </option>
                        {allProviders.map(prov => (
                          <option key={prov.id} value={prov.id}>
                            {prov.nome_exibicao} ({prov.modelo_padrao || prov.provedor})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">SKU do Produto</label>
                      <input
                        type="text"
                        value={sku}
                        onChange={(e) => setSku(e.target.value)}
                        placeholder="Ex: AN-TRI-Z-10"
                        className="w-full p-2.5 border border-slate-300 rounded-lg font-mono font-bold outline-none focus:ring-1 focus:ring-purple-550"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Produto</label>
                      <input
                        type="text"
                        value={produtoNome}
                        onChange={(e) => setProdutoNome(e.target.value)}
                        placeholder="Ex: Anel Trindade"
                        className="w-full p-2.5 border border-slate-300 rounded-lg font-bold outline-none focus:ring-1 focus:ring-purple-550"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          1º Passo: Categoria da Joia (Redator)
                        </label>
                        <button
                          type="button"
                          onClick={() => setActiveSubTab("prompts")}
                          className="text-[9px] font-bold text-blue-600 hover:underline flex items-center gap-0.5"
                        >
                          <Plus size={10} /> Gerenciar
                        </button>
                      </div>
                      <select
                        value={categoriaPromptId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setCategoriaPromptId(id);
                          const found = categoryPrompts.find(p => p.id === id);
                          if (found) setCategoria(found.contexto || found.nome);
                        }}
                        className="w-full p-2.5 border border-slate-300 rounded-lg font-bold bg-white outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        {categoryPrompts.length > 0 ? (
                          categoryPrompts.map(p => (
                            <option key={p.id} value={p.id}>
                              {formatPromptOptionLabel(p)}
                            </option>
                          ))
                        ) : (
                          <option value="">Padrão ({categoria || "Anel"})</option>
                        )}
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          2º Passo: Tipo de Anúncio / Pipe (Normalizador)
                        </label>
                        <button
                          type="button"
                          onClick={() => setActiveSubTab("prompts")}
                          className="text-[9px] font-bold text-purple-600 hover:underline flex items-center gap-0.5"
                        >
                          <Plus size={10} /> Novo Tipo
                        </button>
                      </div>
                      <select
                        value={tipoAnuncioPromptId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setTipoAnuncioPromptId(id);
                          const found = adTypePrompts.find(p => p.id === id);
                          if (found) setTipoAnuncio(found.contexto || found.nome);
                        }}
                        className="w-full p-2.5 border border-purple-300 rounded-lg font-bold bg-purple-50/30 text-slate-800 outline-none focus:ring-1 focus:ring-purple-550"
                      >
                        {adTypePrompts.length > 0 ? (
                          adTypePrompts.map(p => (
                            <option key={p.id} value={p.id}>
                              {formatPromptOptionLabel(p)}
                            </option>
                          ))
                        ) : (
                          <option value="">Padrão ({tipoAnuncio || "Site"})</option>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* RESUMO VISUAL DO PIPELINE SELECIONADO */}
                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex flex-wrap items-center justify-between gap-2 text-[10px]">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-bold text-slate-500 uppercase">Fluxo Selecionado:</span>
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-bold">
                        1º {selectedCategoryAgent ? selectedCategoryAgent.nome : `Redator (${categoria})`}
                      </span>
                      <ArrowRight size={12} className="text-slate-400" />
                      <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-bold">
                        2º {selectedAdTypeAgent ? selectedAdTypeAgent.nome : `Normalizador (${tipoAnuncio})`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveSubTab("prompts")}
                      className="text-purple-600 hover:text-purple-800 font-bold underline"
                    >
                      Editar Pipes ({adTypePrompts.length} tipos de anúncio ativos)
                    </button>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Instruções / Detalhes de Entrada da Peça *</label>
                    <textarea
                      value={descricaoInput}
                      onChange={(e) => setDescricaoInput(e.target.value)}
                      rows={4}
                      placeholder="Digite o banho, tipo de pedraria, se é ajustável, design e qualquer especificação que a IA usará para escrever..."
                      className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-semibold"
                    />
                  </div>

                  <button
                    onClick={runAiPipeline}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-750 text-white font-bold p-3 rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-xs uppercase tracking-wider"
                  >
                    {loading ? (
                      <>
                        <RefreshCw size={15} className="animate-spin mr-1" />
                        {currentStep}
                      </>
                    ) : (
                      <>
                        <Sparkles size={15} /> Gerar Anúncio com Inteligência Artificial ({tipoAnuncio})
                      </>
                    )}
                  </button>
                </div>

                {/* Display raw pipelines outputs if available */}
                {rawOutputs.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-3">
                    <h3 className="font-bold text-slate-700 text-xs">Logs de Processo do Pipeline</h3>
                    <div className="space-y-2 max-h-36 overflow-y-auto custom-scrollbar font-mono text-[9px] text-slate-500">
                      {rawOutputs.map((out, idx) => (
                        <div key={idx} className="bg-slate-50 p-2 rounded border border-slate-200">
                          <strong className="text-slate-700">{out.agent}:</strong>
                          <p className="mt-1 line-clamp-2 italic">"{out.output}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT PREVIEW & EDITOR PANEL */}
              <div className="w-full md:w-1/2 border-t md:border-t-0 md:border-l border-slate-200 bg-white flex flex-col shrink-0 p-6 overflow-y-auto custom-scrollbar gap-4">

                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <h3 className="font-extrabold text-slate-700 text-sm">Preview e Edição Comercial</h3>

                  {result && (
                    <div className="bg-slate-100 rounded-lg p-0.5 flex border border-slate-250">
                      <button
                        onClick={() => setEditMode(true)}
                        className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all ${editMode ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
                          }`}
                      >
                        <Edit3 size={12} className="inline mr-1" /> Editar
                      </button>
                      <button
                        onClick={() => setEditMode(false)}
                        className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all ${!editMode ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
                          }`}
                      >
                        <Eye size={12} className="inline mr-1" /> Preview HTML
                      </button>
                    </div>
                  )}
                </div>

                {result ? (
                  <div className="space-y-4">

                    {editMode ? (
                      /* EDITOR FIELDS */
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nome Comercial (Título)</label>
                            <span className={`text-[9px] font-bold ${result.nome_produto?.length > 60 ? "text-red-500" : "text-slate-400"}`}>
                              {result.nome_produto?.length || 0}/60 caract. (SEO)
                            </span>
                          </div>
                          <input
                            type="text"
                            value={result.nome_produto}
                            onChange={(e) => handleFieldChange("nome_produto", e.target.value)}
                            className="w-full p-2 border border-slate-350 rounded-lg outline-none font-bold focus:ring-1 focus:ring-purple-550"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Descrição Breve (Chamada)</label>
                            <span className={`text-[9px] font-bold ${result.descricao_breve?.length > 150 ? "text-red-500" : "text-slate-400"}`}>
                              {result.descricao_breve?.length || 0}/150 caract.
                            </span>
                          </div>
                          <textarea
                            rows={2}
                            value={result.descricao_breve}
                            onChange={(e) => handleFieldChange("descricao_breve", e.target.value)}
                            className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-purple-555 font-semibold"
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Palavras-Chave (Separadas por vírgula)</label>
                          <input
                            type="text"
                            value={result.palavras_chave?.join(", ")}
                            onChange={(e) => handleKeywordsChange(e.target.value)}
                            className="w-full p-2 border border-slate-355 rounded-lg outline-none focus:ring-1 focus:ring-purple-555"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Meta Title (Google)</label>
                            <span className={`text-[9px] font-bold ${result.meta_title?.length > 60 ? "text-red-500" : "text-slate-400"}`}>
                              {result.meta_title?.length || 0}/60 caract. (SEO)
                            </span>
                          </div>
                          <input
                            type="text"
                            value={result.meta_title}
                            onChange={(e) => handleFieldChange("meta_title", e.target.value)}
                            className="w-full p-2 border border-slate-355 rounded-lg outline-none focus:ring-1 focus:ring-purple-555 font-mono"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Meta Description (Google)</label>
                            <span className={`text-[9px] font-bold ${result.meta_description?.length > 160 ? "text-red-550" : "text-slate-400"}`}>
                              {result.meta_description?.length || 0}/160 caract. (SEO)
                            </span>
                          </div>
                          <textarea
                            rows={2}
                            value={result.meta_description}
                            onChange={(e) => handleFieldChange("meta_description", e.target.value)}
                            className="w-full p-2 border border-slate-355 rounded-lg outline-none focus:ring-1 focus:ring-purple-555 font-mono"
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição Completa (HTML)</label>
                          <textarea
                            rows={8}
                            value={result.descricao_completa}
                            onChange={(e) => handleFieldChange("descricao_completa", e.target.value)}
                            className="w-full p-2 border border-slate-355 rounded-lg outline-none focus:ring-1 focus:ring-purple-555 font-mono text-[11px] leading-relaxed"
                          />
                        </div>
                      </div>
                    ) : (
                      /* HTML VISUAL PREVIEW */
                      <div className="space-y-4">
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                          <div>
                            <h1 className="text-sm font-extrabold text-slate-800">{result.nome_produto}</h1>
                            <p className="text-slate-500 text-[10px] mt-0.5 italic">{result.descricao_breve}</p>
                          </div>

                          <div className="border-t border-slate-200/80 pt-3">
                            {/* Render HTML output from AI */}
                            <div
                              className="prose prose-xs font-semibold leading-relaxed text-slate-700"
                              dangerouslySetInnerHTML={{ __html: result.descricao_completa }}
                            />
                          </div>

                          {result.palavras_chave && (
                            <div className="flex flex-wrap gap-1 border-t border-slate-200/80 pt-3">
                              {result.palavras_chave.map((k, kIdx) => (
                                <span key={kIdx} className="px-2 py-0.5 bg-slate-100 text-slate-650 rounded-full text-[9px] font-medium border border-slate-200">
                                  {k}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Google Snippet SEO Preview */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1 shadow-sm font-sans">
                          <span className="text-[10px] text-slate-400 block font-normal font-mono">https://www.semprejoias.com.br &gt; produto</span>
                          <a href="#preview" onClick={(e) => e.preventDefault()} className="text-blue-800 hover:underline text-sm font-medium block leading-tight">{result.meta_title || result.nome_produto}</a>
                          <p className="text-slate-600 text-[11px] leading-snug">{result.meta_description || result.descricao_breve}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-100">
                      <button
                        onClick={handleSaveAdvert}
                        className="flex-1 bg-purple-600 hover:bg-purple-755 text-white font-bold py-3.5 rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5 uppercase tracking-wide"
                      >
                        <Save size={14} /> Salvar Histórico
                      </button>
                    </div>

                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-400 font-semibold text-center py-12">
                    Aguardando a execução da Inteligência Artificial.
                  </div>
                )}

              </div>

            </div>
            ) : (
            <div className="h-full overflow-y-auto p-6 bg-slate-50 custom-scrollbar">
              <IaPromptsSubTab onPromptsChange={fetchPrompts} />
            </div>
        )}
          </div>
    </div>
      );
}
